import isMeetingCancellation from './isMeetingCancellation';
import isMeetingRequest from './isMeetingRequest';
import isMeetingResponse from './isMeetingResponse';
import MeetingMessageActionEntrySource from '../store/schema/MeetingMessageActionEntrySource';
import { logUsage } from 'owa-analytics-actions';
import MeetingMessage from 'owa-service/lib/contract/MeetingMessage';
import MeetingRequestMessageType from 'owa-service/lib/contract/MeetingRequestMessageType';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { getUserConfiguration } from 'owa-session-store';
import {
    markSessionStart,
    getSessionDuration,
    markSessionEnd,
} from './sessionDurationTelemetryHelpers';
import { CalendarEvent } from 'owa-calendar-types';
import { MeetingMessageActionType } from './MeetingMessageActionType';

type ViewType =
    | 'MeetingInvite'
    | 'MeetingInformationalUpdate'
    | 'MeetingUpdate'
    | 'MeetingCancelation'
    | 'MeetingResponse'
    | 'CalendarCard';

type ActionNecessary = 'None' | 'ResponseRequired' | 'Cancellation';

type AttendeeRole = 'Organizer' | 'Delegate' | 'Required' | 'Optional';

type EventType = 'Instance' | 'Series' | 'Single';

function logMeetingMessageActionCommon(
    interactionType: MeetingMessageActionType,
    itemId: string,
    entrySource: MeetingMessageActionEntrySource,
    eventType: EventType,
    attendeeCount: number,
    viewType: ViewType,
    actionNecessary: ActionNecessary,
    attendeeType: AttendeeRole,
    propertyNames?: string[]
) {
    // Mark the session end if it hasn't ended yet
    let timeInMessage = 0;
    if (interactionType == 'ViewOpened') {
        markSessionStart(itemId, entrySource);
    } else if (interactionType == 'ViewClosed') {
        timeInMessage = markSessionEnd(itemId, entrySource);
    } else {
        timeInMessage = getSessionDuration(itemId, entrySource);
    }

    const readingSetting: ReactListViewType = getUserConfiguration().UserOptions
        .GlobalListViewTypeReact;
    const propertyNamesList: string = (propertyNames || []).join(';');
    const readingPanePosition = getUserConfiguration().UserOptions.GlobalReadingPanePositionReact;

    logUsage('meetingMessageAction', {
        interactionType,
        viewType,
        entrySource,
        attendeeType,
        readingSetting,
        eventType,
        attendeeCount,
        timeInMessage,
        propertyNamesList,
        readingPanePosition,
        actionNecessary,
    });
}

export function logMeetingMessageActionForInvite(
    interactionType: MeetingMessageActionType,
    item: MeetingMessage,
    entrySource: MeetingMessageActionEntrySource,
    propertyNames?: string[]
) {
    const eventType: EventType = item.RecurrenceId
        ? 'Instance'
        : (item as MeetingRequestMessageType).Recurrence // Series information is only on Requests
        ? 'Series'
        : 'Single';

    // TODO VSO: 46665 Update logging to reflect HideAttendees setting
    const attendeeCount: number =
        (item.ToRecipients || []).length +
        (item.CcRecipients || []).length +
        (item.BccRecipients || []).length;

    const viewType = getMeetingMessageViewType(item);
    const actionNecessary = getMeetingMessageActionNecessary(item);
    const attendeeType = getMeetingMessageAttendeeRole(item);

    logMeetingMessageActionCommon(
        interactionType,
        item.ItemId.Id,
        entrySource,
        eventType,
        attendeeCount,
        viewType,
        actionNecessary,
        attendeeType,
        propertyNames
    );
}

function getMeetingMessageViewType(item: MeetingMessage): ViewType {
    if (isMeetingRequest(item.ItemClass)) {
        const request = item as MeetingRequestMessageType;
        switch (request.MeetingRequestType) {
            case 'NewMeetingRequest':
                return 'MeetingInvite';
            case 'FullUpdate':
                return 'MeetingUpdate';
            case 'InformationalUpdate':
                return 'MeetingInformationalUpdate';
        }
    }
    if (isMeetingCancellation(item.ItemClass)) {
        return 'MeetingCancelation';
    }
    if (isMeetingResponse(item.ItemClass)) {
        return 'MeetingResponse';
    }
    return null;
}

function getMeetingMessageActionNecessary(item: MeetingMessage): ActionNecessary {
    if (isMeetingRequest(item.ItemClass)) {
        const request = item as MeetingRequestMessageType;
        switch (request.MeetingRequestType) {
            case 'NewMeetingRequest':
                return 'ResponseRequired';
            case 'FullUpdate':
                return 'ResponseRequired';
        }
    }
    if (isMeetingCancellation(item.ItemClass)) {
        return 'Cancellation';
    }
    return 'None';
}

function getMeetingMessageAttendeeRole(item: MeetingMessage): AttendeeRole {
    if (item.IsDelegated) {
        return 'Delegate';
    }
    if (item.IsOrganizer) {
        return 'Organizer';
    }

    if (
        item.CcRecipients &&
        item.CcRecipients.some(
            cc =>
                cc.EmailAddress.toLowerCase() ===
                getUserConfiguration().SessionSettings.UserEmailAddress.toLowerCase()
        )
    ) {
        return 'Optional';
    }
    return 'Required';
}

export function logMeetingMessageActionForEvent(
    interactionType: MeetingMessageActionType,
    item: CalendarEvent,
    entrySource: MeetingMessageActionEntrySource,
    propertyNames?: string[]
) {
    const eventType = getCalendarEventEventType(item);

    // TODO VSO: 46665 Update logging to reflect HideAttendees setting
    const attendeeCount: number =
        (item.RequiredAttendees || []).length + (item.OptionalAttendees || []).length;

    const actionNecessary = getCalendarEventActionNecessary(item);
    const attendeeType = getCalendarEventAttendeeRole(item);

    logMeetingMessageActionCommon(
        interactionType,
        item.ItemId.Id,
        entrySource,
        eventType,
        attendeeCount,
        'CalendarCard',
        actionNecessary,
        attendeeType,
        propertyNames
    );
}

function getCalendarEventEventType(item: CalendarEvent): EventType {
    switch (item.CalendarItemType) {
        case 'Single':
            return 'Single';
        case 'Occurrence':
            return 'Instance';
        case 'Exception':
            return 'Instance';
        case 'RecurringMaster':
            return 'Series';
    }
    return null;
}

function getCalendarEventActionNecessary(item: CalendarEvent): ActionNecessary {
    if (!item.IsOrganizer) {
        if (item.IsCancelled) {
            return 'Cancellation';
        } else if (
            !item.AppointmentReplyTime &&
            !['Accept', 'Tentative', 'Decline'].includes(item.ResponseType)
        ) {
            return 'ResponseRequired';
        }
    }
    return 'None';
}

function getCalendarEventAttendeeRole(item: CalendarEvent): AttendeeRole {
    // TODO VSO 55115 - Add logging when delegate support for meeting card is added
    if (item.IsOrganizer) {
        return 'Organizer';
    }

    if (
        item.OptionalAttendees &&
        item.OptionalAttendees.some(
            cc =>
                cc.Mailbox &&
                cc.Mailbox.EmailAddress.toLowerCase() ===
                    getUserConfiguration().SessionSettings.UserEmailAddress.toLowerCase()
        )
    ) {
        return 'Optional';
    }
    return 'Required';
}
